import {
  KeyboardEvent,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { X } from "lucide-react";
import "./styles.css";

const MIN_YEAR = 1926;
const MAX_YEAR = 2026;
const WHEEL_ITEM_HEIGHT = 44;

const keepInputVisible = (element: HTMLInputElement | HTMLTextAreaElement) => {
  const scrollIntoView = () => {
    element.scrollIntoView({
      block: "center",
      inline: "nearest",
      behavior: "auto",
    });
  };

  window.requestAnimationFrame(scrollIntoView);
  window.setTimeout(scrollIntoView, 180);
};

export type WheelOption = {
  value: number;
  label: string;
};

type OptionListOption =
  | string
  | { value: string; label: string; description?: string; icon?: string };

export type DateParts = {
  day: number;
  month: number;
  year: number;
};

export const months = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

export const normalizeDateParts = (value: string): DateParts => {
  const fallback = { day: 12, month: 5, year: 1995 };

  if (!value) {
    return fallback;
  }

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return {
      day: Number(isoMatch[3]),
      month: Number(isoMatch[2]),
      year: Number(isoMatch[1]),
    };
  }

  const displayMatch = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (displayMatch) {
    return {
      day: Number(displayMatch[1]),
      month: Number(displayMatch[2]),
      year: Number(displayMatch[3]),
    };
  }

  return fallback;
};

export const toIsoDate = (day: number, month: number, year: number): string =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export const formatDisplayDate = (value: string): string => {
  if (!value) {
    return "";
  }

  const { day, month, year } = normalizeDateParts(value);
  return `${String(day).padStart(2, "0")}.${String(month).padStart(2, "0")}.${year}`;
};

export const clampDay = (day: number, month: number, year: number): number => {
  const maxDay = new Date(year, month, 0).getDate();
  return Math.min(day, maxDay);
};

export const createNumberOptions = (min: number, max: number, suffix = "") =>
  Array.from({ length: max - min + 1 }, (_, index) => {
    const value = min + index;
    return {
      value,
      label: suffix ? `${value} ${suffix}` : String(value),
    };
  });

export const ProfileInfoForm = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => <div className={`auth-info-form ${className}`.trim()}>{children}</div>;

export const InfoField = ({
  icon,
  label,
  value,
  placeholder,
  trailing,
  onClick,
  onClear,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  placeholder: string;
  trailing?: ReactNode;
  onClick: () => void;
  onClear?: () => void;
}) => (
  <button className="auth-info-card" type="button" onClick={onClick}>
    <span className="auth-info-card-icon">{icon}</span>
    <span className="auth-info-card-text">
      <span className="auth-info-card-label">{label}</span>
      <strong className={!value ? "is-placeholder" : ""}>
        {value || placeholder}
      </strong>
    </span>
    {trailing ? (
      <span className="auth-info-card-trailing">{trailing}</span>
    ) : (
      <span
        className="auth-info-card-clear"
        role="button"
        tabIndex={-1}
        onClick={(event) => {
          event.stopPropagation();
          onClear?.();
        }}
      >
        <X size={18} strokeWidth={2.2} />
      </span>
    )}
  </button>
);

export const GenderSwitchCard = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="auth-info-gender-card">
    <span className="auth-info-card-label">Пол</span>
    <div className="auth-info-gender-switch">
      <button
        className={value === "Мужской" ? "is-active" : ""}
        type="button"
        onClick={() => onChange("Мужской")}
      >
        Мужской
      </button>
      <button
        className={value === "Женский" ? "is-active" : ""}
        type="button"
        onClick={() => onChange("Женский")}
      >
        Женский
      </button>
    </div>
  </div>
);

export const InfoBottomSheet = ({
  title,
  modifier,
  children,
  onClose,
  onCommit,
}: {
  title: string;
  modifier?: string;
  children: ReactNode;
  onClose: () => void;
  onCommit: () => void;
}) => (
  <div className="auth-info-overlay" onMouseDown={onClose}>
    <section
      className={`auth-info-sheet ${modifier ? `auth-info-sheet--${modifier}` : ""}`.trim()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <header className="auth-info-sheet-header">
        <span />
        <strong>{title}</strong>
        <button type="button" onClick={onCommit}>
          Готово
        </button>
      </header>
      {children}
    </section>
  </div>
);

export const TextSheetInput = ({
  value,
  placeholder,
  onChange,
}: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useLayoutEffect(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    const focusInput = () => {
      input.focus();

      try {
        input.setSelectionRange(input.value.length, input.value.length);
      } catch {
        // Selection ranges are not available for every native input mode.
      }

      keepInputVisible(input);
    };

    focusInput();

    const rafId = window.requestAnimationFrame(focusInput);
    const timerId = window.setTimeout(focusInput, 120);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timerId);
    };
  }, []);

  return (
    <input
      ref={inputRef}
      autoFocus
      className="auth-info-sheet-input"
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onFocus={(event) => keepInputVisible(event.currentTarget)}
    />
  );
};

export const OptionListPicker = ({
  options,
  value,
  onChange,
}: {
  options: OptionListOption[];
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="auth-info-option-list">
    {options.map((option) => {
      const optionValue = typeof option === "string" ? option : option.value;
      const optionLabel = typeof option === "string" ? option : option.label;
      const optionDescription =
        typeof option === "string" ? "" : option.description;
      const optionIcon = typeof option === "string" ? "" : option.icon;
      const className = [
        optionValue === value ? "is-active" : "",
        optionIcon ? "has-icon" : "",
      ]
        .filter(Boolean)
        .join(" ");

      return (
        <button
          key={optionValue}
          className={className}
          type="button"
          onClick={() => onChange(optionValue)}
        >
          {optionIcon && (
            <span className="auth-info-option-icon-frame" aria-hidden="true">
              <img src={optionIcon} alt="" />
            </span>
          )}
          <span className="auth-info-option-copy">
            <span className="auth-info-option-label">{optionLabel}</span>
            {optionDescription && (
              <span className="auth-info-option-description">
                {optionDescription}
              </span>
            )}
          </span>
        </button>
      );
    })}
  </div>
);

export const DateWheelPicker = ({
  value,
  onChange,
}: {
  value: DateParts;
  onChange: (value: DateParts) => void;
}) => {
  const years = useMemo(
    () =>
      Array.from(
        { length: MAX_YEAR - MIN_YEAR + 1 },
        (_, index) => MIN_YEAR + index,
      ).reverse(),
    [],
  );
  const safeDay = clampDay(value.day, value.month, value.year);
  const dayOptions = createNumberOptions(
    1,
    new Date(value.year, value.month, 0).getDate(),
  );
  const monthOptions = months.map((month, index) => ({
    value: index + 1,
    label: month,
  }));
  const yearOptions = years.map((year) => ({
    value: year,
    label: String(year),
  }));

  return (
    <div className="auth-info-date-picker">
      <WheelPicker
        ariaLabel="День рождения"
        options={dayOptions}
        value={safeDay}
        onChange={(day) =>
          onChange({
            ...value,
            day,
          })
        }
      />
      <WheelPicker
        ariaLabel="Месяц рождения"
        options={monthOptions}
        value={value.month}
        onChange={(month) =>
          onChange({
            ...value,
            month,
            day: clampDay(value.day, month, value.year),
          })
        }
      />
      <WheelPicker
        ariaLabel="Год рождения"
        options={yearOptions}
        value={value.year}
        onChange={(year) =>
          onChange({
            ...value,
            year,
            day: clampDay(value.day, value.month, year),
          })
        }
      />
      <div className="auth-info-date-labels">
        <span>День</span>
        <span>Месяц</span>
        <span>Год</span>
      </div>
    </div>
  );
};

export const WheelPicker = ({
  ariaLabel,
  className = "",
  options,
  value,
  onChange,
}: {
  ariaLabel: string;
  className?: string;
  options: WheelOption[];
  value: number;
  onChange: (value: number) => void;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollTimer = useRef<number | null>(null);

  useEffect(() => {
    const index = options.findIndex((option) => option.value === value);
    if (index < 0 || !ref.current) {
      return;
    }

    ref.current.scrollTo({
      top: index * WHEEL_ITEM_HEIGHT,
      behavior: "auto",
    });
  }, [options, value]);

  useEffect(() => {
    return () => {
      if (scrollTimer.current) {
        window.clearTimeout(scrollTimer.current);
      }
    };
  }, []);

  const handleScroll = () => {
    if (scrollTimer.current) {
      window.clearTimeout(scrollTimer.current);
    }

    scrollTimer.current = window.setTimeout(() => {
      if (!ref.current) {
        return;
      }

      const index = Math.min(
        options.length - 1,
        Math.max(0, Math.round(ref.current.scrollTop / WHEEL_ITEM_HEIGHT)),
      );
      const nextValue = options[index]?.value;

      if (nextValue !== undefined && nextValue !== value) {
        onChange(nextValue);
      }
    }, 80);
  };

  return (
    <div className={`auth-info-wheel-picker-frame ${className}`.trim()}>
      <div
        ref={ref}
        className="auth-info-wheel-picker"
        aria-label={ariaLabel}
        role="listbox"
        tabIndex={0}
        onScroll={handleScroll}
      >
        {options.map((option) => (
          <button
            key={option.value}
            className={option.value === value ? "is-active" : ""}
            type="button"
            role="option"
            aria-selected={option.value === value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

type WeightScalePickerProps = {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
};

export const WeightScalePicker = ({
  value,
  min,
  max,
  step,
  onChange,
}: WeightScalePickerProps) => {
  const tickWidth = 12;
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollTimer = useRef<number | null>(null);

  const decimals = useMemo(() => {
    const [, fraction = ""] = String(step).split(".");
    return fraction.length;
  }, [step]);

  const roundToStepPrecision = (nextValue: number) =>
    Number(nextValue.toFixed(decimals));

  const formatWeightLabel = (nextValue: number) =>
    nextValue.toFixed(1).replace(".", ",");

  const tickCount = Math.round((max - min) / step) + 1;

  const getValueByIndex = (index: number) =>
    roundToStepPrecision(min + index * step);

  const getIndexByValue = (nextValue: number) =>
    Math.min(tickCount - 1, Math.max(0, Math.round((nextValue - min) / step)));

  const ticks = useMemo(
    () =>
      Array.from({ length: tickCount }, (_, index) => {
        const numericValue = getValueByIndex(index);
        const isWhole =
          Math.abs(numericValue - Math.round(numericValue)) < 0.001;

        return {
          value: numericValue,
          isWhole,
          label: isWhole ? String(Math.round(numericValue)) : "",
        };
      }),
    [tickCount, min, step, decimals],
  );

  useEffect(() => {
    if (!ref.current) return;

    const index = getIndexByValue(value);

    ref.current.scrollTo({
      left: index * tickWidth,
      behavior: "auto",
    });
  }, [value, tickWidth, tickCount, min, step]);

  useEffect(() => {
    return () => {
      if (scrollTimer.current) {
        window.clearTimeout(scrollTimer.current);
      }
    };
  }, []);

  const handleScroll = () => {
    if (scrollTimer.current) {
      window.clearTimeout(scrollTimer.current);
    }

    scrollTimer.current = window.setTimeout(() => {
      if (!ref.current) return;

      const index = Math.min(
        tickCount - 1,
        Math.max(0, Math.round(ref.current.scrollLeft / tickWidth)),
      );

      const nextValue = getValueByIndex(index);

      if (nextValue !== roundToStepPrecision(value)) {
        onChange(nextValue);
      }

      ref.current.scrollTo({
        left: index * tickWidth,
        behavior: "smooth",
      });
    }, 80);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!ref.current) return;

    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      return;
    }

    event.preventDefault();

    const direction = event.key === "ArrowRight" ? 1 : -1;
    const currentIndex = getIndexByValue(value);

    const nextIndex = Math.min(
      tickCount - 1,
      Math.max(0, currentIndex + direction),
    );

    const nextValue = getValueByIndex(nextIndex);

    ref.current.scrollTo({
      left: nextIndex * tickWidth,
      behavior: "smooth",
    });

    onChange(nextValue);
  };

  return (
    <div className="auth-info-weight-picker">
      <div className="auth-info-weight-value">
        <strong>{formatWeightLabel(value)}</strong>
        <span>кг</span>
      </div>

      <div className="auth-info-weight-marker" aria-hidden="true" />

      <div
        ref={ref}
        className="auth-info-weight-scale"
        aria-label="Выбор веса"
        role="slider"
        tabIndex={0}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
      >
        <div className="auth-info-weight-ticks">
          {ticks.map((tick) => (
            <span
              key={tick.value}
              className={tick.isWhole ? "is-major" : undefined}
              aria-hidden="true"
            >
              {tick.label && <em>{tick.label}</em>}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
